/**
 * Offline Sync Service
 * 
 * High-level service for synchronizing offline data with server.
 * Wraps offlineManager and syncQueue to provide a unified sync interface.
 */

import { offlineManager } from './offlineManager';
import { syncQueue, type SyncAction } from './syncQueue';
import { networkMonitor, type NetworkStatus } from './networkMonitor';
import { logger } from './logger';
import { getUnresolvedConflicts, getConflictCount } from './conflictResolution';

export type SyncStatus = {
  lastSync: Date;
  pendingChanges: number;
  failedChanges: number;
  conflicts: number;
  isOnline: boolean;
  isSyncing: boolean;
  syncProgress?: {
    completed: number;
    total: number;
  };
};

export type OfflineChange = {
  type: 'create' | 'update' | 'delete';
  collection: string;
  docId: string;
  data: any;
  tripId?: string;
  userId?: string;
};

/**
 * Get current sync status
 */
export const getSyncStatus = async (): Promise<SyncStatus> => {
  try {
    // IMPORTANT:
    // Avoid networkMonitor.getStatus() here. That method performs a fresh NetInfo fetch,
    // which can trigger monitor listeners again. Since subscribeToSyncStatus itself is driven
    // by networkMonitor listeners, calling getStatus() here creates a feedback loop on web.
    const isOnline = networkMonitor.isOnline();
    const queueSize = await offlineManager.getQueueSize();
    const deadLetterActions = await syncQueue.getDeadLetterActions();
    const metadata = await offlineManager.getMetadata();
    const isSyncing = offlineManager.isSyncing();

    // Get conflicts count
    const conflicts = await getConflictCount();

    return {
      lastSync: metadata?.lastSync ? new Date(metadata.lastSync) : new Date(0),
      pendingChanges: queueSize,
      failedChanges: deadLetterActions.length,
      conflicts,
      isOnline,
      isSyncing,
    };
  } catch (error) {
    logger.error('[OfflineSync] Error getting sync status', error instanceof Error ? error : new Error(String(error)));
    return {
      lastSync: new Date(0),
      pendingChanges: 0,
      failedChanges: 0,
      conflicts: 0,
      // Fail open on connectivity so transient queue/metadata errors don't flash false offline UI.
      isOnline: networkMonitor.isOnline(),
      isSyncing: false,
    };
  }
};

/**
 * Sync all offline data when online
 */
export const syncOfflineData = async (): Promise<SyncStatus> => {
  try {
    const networkStatus = await networkMonitor.getStatus();

    // Only sync when monitor confirms real online state (connected + reachable),
    // not just interface connectivity.
    if (!networkStatus.isConnected || !networkMonitor.isOnline()) {
      logger.info('[OfflineSync] Device is offline, cannot sync');
      return await getSyncStatus();
    }

    logger.info('[OfflineSync] Starting sync...');
    
    // Process sync queue
    await offlineManager.syncWhenOnline();
    
    // Update last sync time
    const metadata = await offlineManager.getMetadata();
    if (metadata) {
      await offlineManager.getMetadata(); // This will be updated by syncWhenOnline
    }

    logger.info('[OfflineSync] Sync completed');
    return await getSyncStatus();
  } catch (error) {
    logger.error('[OfflineSync] Error syncing offline data', error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
};

/**
 * Queue an offline change for sync
 */
export const queueOfflineChange = async (
  change: OfflineChange
): Promise<void> => {
  try {
    // Map change type to sync action type
    const actionTypeMap: Record<string, SyncAction['type']> = {
      'create_expense': 'create_expense',
      'update_expense': 'update_expense',
      'delete_expense': 'delete_expense',
      'create_booking': 'create_booking',
      'update_booking': 'update_booking',
      'delete_booking': 'delete_booking',
      'update_trip': 'update_trip',
      'create_itinerary_item': 'create_itinerary_item',
      'update_itinerary_item': 'update_itinerary_item',
      'delete_itinerary_item': 'delete_itinerary_item',
      'create_comment': 'create_comment',
      'update_comment': 'update_comment',
      'delete_comment': 'delete_comment',
    };

    // Build action type from change
    const actionType = actionTypeMap[`${change.type}_${change.collection}`] || 
      (change.type === 'create' ? 'create_expense' : 
       change.type === 'update' ? 'update_expense' : 
       'delete_expense') as SyncAction['type'];

    // Build payload
    const payload: Record<string, unknown> = {
      collection: change.collection,
      docId: change.docId,
      data: change.data,
    };

    // Queue the action
    await offlineManager.queueAction({
      type: actionType,
      payload,
      tripId: change.tripId,
      userId: change.userId,
    });

    logger.info('[OfflineSync] Queued offline change', {
      type: change.type,
      collection: change.collection,
      docId: change.docId,
    });
  } catch (error) {
    logger.error('[OfflineSync] Error queueing offline change', error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
};

/**
 * Subscribe to sync status changes
 */
export const subscribeToSyncStatus = (
  callback: (status: SyncStatus) => void
): (() => void) => {
  let isSubscribed = true;
  let updateSeq = 0;
  
  const updateStatus = async () => {
    if (!isSubscribed) return;
    const seq = ++updateSeq;
    const status = await getSyncStatus();
    // Ignore stale async responses so UI doesn't regress to older counts/state.
    if (!isSubscribed || seq !== updateSeq) return;
    callback(status);
  };

  // Subscribe to network changes
  const unsubscribeNetwork = networkMonitor.addListener(async (networkStatus) => {
    await updateStatus();
  });

  // Subscribe to sync queue processing changes
  const unsubscribeSync = offlineManager.onSyncProcessingChange(async (isProcessing) => {
    await updateStatus();
  });

  const unsubscribeQueue = syncQueue.subscribeToQueueChanges(() => {
    void updateStatus();
  });

  // Initial status
  updateStatus();

  // Return unsubscribe function
  return () => {
    isSubscribed = false;
    unsubscribeNetwork();
    unsubscribeSync();
    unsubscribeQueue();
  };
};

/**
 * Force sync (manual trigger)
 */
export const forceSync = async (): Promise<SyncStatus> => {
  logger.info('[OfflineSync] Force sync requested');
  return await syncOfflineData();
};

/**
 * Clear sync queue (use with caution)
 */
export const clearSyncQueue = async (): Promise<void> => {
  try {
    const queue = await offlineManager.getQueuedActions();
    logger.warn(`[OfflineSync] Clearing ${queue.length} queued actions`);
    // This would need to be implemented in syncQueue
    // For now, we'll log a warning
    logger.warn('[OfflineSync] Clear queue not fully implemented');
  } catch (error) {
    logger.error('[OfflineSync] Error clearing sync queue', error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
};

